import { useMemo } from "react";
import type { SwmmProject } from "../model/inp/types.ts";
import type { SimulationResults } from "../model/results/simulationResults.ts";
import { seriesMapFor } from "../model/results/resultsVariables.ts";
import { colorsAtTime, findSeriesPeak } from "./resultsAnimationColors.ts";
import { THEMATIC_ATTRIBUTES } from "../model/inp/thematicAttributes.ts";
import { buildThematicColors } from "./thematicColors.ts";
import { findMapQueryMatches } from "./mapQuery.ts";
import type { useResultsAnimation } from "./useResultsAnimation.ts";
import type { useThematicMap } from "./useThematicMap.ts";
import type { useMapQuery } from "./useMapQuery.ts";
import type { useNetworkTrace } from "./useNetworkTrace.ts";

// Second-audit finding: the previous pair (Material pink `#e91e63` /
// Material orange `#ff9800`) were an isolated palette island unrelated to
// either mapTheme.ts's Okabe-Ito set or chartTheme.ts's `CHART_PALETTE`.
// Swapped for two Okabe-Ito hues that aren't already used elsewhere on the
// map: reddish purple for query matches (no collision with LINK_INK's blue,
// SELECTION_RING_OUTER's amber, MULTI_SELECT_COLOR's yellow,
// SUBCATCHMENT_COLOR's bluish green, or RAINGAGE_COLOR's sky blue) and
// vermillion for trace results (deliberately distinct from the reddish
// purple so a trace and a query match never read as the same thing).
const MAP_QUERY_HIGHLIGHT = "#CC79A7";
const NETWORK_TRACE_HIGHLIGHT = "#D55E00";

/**
 * Per-frame color maps for NetworkMap/GeoreferencedMap's node/link/
 * subcatchment overlays — results animation, thematic map, map query and
 * network trace all feed the same three props, mutually exclusive by
 * construction (the toolbar toggles that open one close the others), merged
 * with a plain `??` at the call site. Pure derived data: no state of its
 * own, just `useMemo` composed over the four overlay hooks' own state plus
 * `project`/`results`.
 */
export function useMapOverlayColors(
  project: SwmmProject | null,
  results: SimulationResults | null,
  resultsAnimation: ReturnType<typeof useResultsAnimation>,
  thematicMap: ReturnType<typeof useThematicMap>,
  mapQuery: ReturnType<typeof useMapQuery>,
  networkTrace: ReturnType<typeof useNetworkTrace>
) {
  // Falls back to an empty Map for a variable key that somehow doesn't
  // resolve for its own category, so `findSeriesPeak`/`colorsAtTime` below
  // never need an `undefined` case.
  const EMPTY_SERIES_MAP = useMemo(() => new Map<string, number[]>(), []);
  const nodeSeriesMap = useMemo(
    () =>
      results
        ? (seriesMapFor(results, "node", resultsAnimation.nodeVariable) ?? EMPTY_SERIES_MAP)
        : EMPTY_SERIES_MAP,
    [results, resultsAnimation.nodeVariable, EMPTY_SERIES_MAP]
  );
  const linkSeriesMap = useMemo(
    () =>
      results
        ? (seriesMapFor(results, "link", resultsAnimation.linkVariable) ?? EMPTY_SERIES_MAP)
        : EMPTY_SERIES_MAP,
    [results, resultsAnimation.linkVariable, EMPTY_SERIES_MAP]
  );
  const subcatchmentSeriesMap = useMemo(
    () =>
      results
        ? (seriesMapFor(results, "subcatchment", resultsAnimation.subcatchmentVariable) ??
          EMPTY_SERIES_MAP)
        : EMPTY_SERIES_MAP,
    [results, resultsAnimation.subcatchmentVariable, EMPTY_SERIES_MAP]
  );
  // Each series' peak is fixed for the whole simulation — memoized
  // separately so it's computed once, not rescanned on every animation
  // frame. Re-derives when the chosen variable changes, since a different
  // variable has a different global peak the color scale must renormalize
  // against.
  const nodeVariablePeak = useMemo(() => findSeriesPeak(nodeSeriesMap), [nodeSeriesMap]);
  const linkVariablePeak = useMemo(() => findSeriesPeak(linkSeriesMap), [linkSeriesMap]);
  const subcatchmentVariablePeak = useMemo(
    () => findSeriesPeak(subcatchmentSeriesMap),
    [subcatchmentSeriesMap]
  );
  const resultsAnimationNodeColors = useMemo(
    () =>
      resultsAnimation.open && resultsAnimation.showNodes
        ? colorsAtTime(nodeSeriesMap, resultsAnimation.index, nodeVariablePeak)
        : undefined,
    [
      resultsAnimation.open,
      resultsAnimation.showNodes,
      nodeSeriesMap,
      resultsAnimation.index,
      nodeVariablePeak,
    ]
  );
  const resultsAnimationLinkColors = useMemo(
    () =>
      resultsAnimation.open && resultsAnimation.showLinks
        ? colorsAtTime(linkSeriesMap, resultsAnimation.index, linkVariablePeak)
        : undefined,
    [
      resultsAnimation.open,
      resultsAnimation.showLinks,
      linkSeriesMap,
      resultsAnimation.index,
      linkVariablePeak,
    ]
  );
  const resultsAnimationSubcatchmentColors = useMemo(
    () =>
      resultsAnimation.open && resultsAnimation.showSubcatchments
        ? colorsAtTime(subcatchmentSeriesMap, resultsAnimation.index, subcatchmentVariablePeak)
        : undefined,
    [
      resultsAnimation.open,
      resultsAnimation.showSubcatchments,
      subcatchmentSeriesMap,
      resultsAnimation.index,
      subcatchmentVariablePeak,
    ]
  );

  const thematicAttribute = useMemo(
    () => THEMATIC_ATTRIBUTES.find((a) => a.key === thematicMap.attribute) ?? null,
    [thematicMap.attribute]
  );
  const thematicColorResult = useMemo(
    () =>
      thematicMap.open && thematicAttribute && project
        ? buildThematicColors(thematicAttribute.read(project.rawSections, results))
        : null,
    [thematicMap.open, thematicAttribute, project, results]
  );
  const thematicNodeColors =
    thematicColorResult && thematicAttribute?.category === "node"
      ? thematicColorResult.colors
      : undefined;
  const thematicLinkColors =
    thematicColorResult && thematicAttribute?.category === "link"
      ? thematicColorResult.colors
      : undefined;
  const thematicSubcatchmentColors =
    thematicColorResult && thematicAttribute?.category === "subcatchment"
      ? thematicColorResult.colors
      : undefined;

  // A single fixed color for every matching element (a query is yes/no, not
  // a gradient), same mutual-exclusivity as the thematic-map colors above.
  const mapQueryAttributeDef = useMemo(
    () => THEMATIC_ATTRIBUTES.find((a) => a.key === mapQuery.attribute) ?? null,
    [mapQuery.attribute]
  );
  const mapQueryValues = useMemo(
    () =>
      mapQuery.open && mapQueryAttributeDef && project
        ? mapQueryAttributeDef.read(project.rawSections, null)
        : null,
    [mapQuery.open, mapQueryAttributeDef, project]
  );
  const mapQueryThreshold = Number(mapQuery.thresholdInput);
  const mapQueryMatches = useMemo(
    () =>
      mapQueryValues && Number.isFinite(mapQueryThreshold)
        ? findMapQueryMatches(mapQueryValues, mapQuery.comparator, mapQueryThreshold)
        : null,
    [mapQueryValues, mapQuery.comparator, mapQueryThreshold]
  );
  const mapQueryColors = useMemo(() => {
    if (!mapQueryMatches) return undefined;
    const colors = new Map<string, string>();
    for (const name of mapQueryMatches) colors.set(name, MAP_QUERY_HIGHLIGHT);
    return colors;
  }, [mapQueryMatches]);
  const mapQueryNodeColors =
    mapQueryColors && mapQueryAttributeDef?.category === "node" ? mapQueryColors : undefined;
  const mapQueryLinkColors =
    mapQueryColors && mapQueryAttributeDef?.category === "link" ? mapQueryColors : undefined;
  const mapQuerySubcatchmentColors =
    mapQueryColors && mapQueryAttributeDef?.category === "subcatchment"
      ? mapQueryColors
      : undefined;

  const networkTraceNodeColors = useMemo(() => {
    if (!networkTrace.result) return undefined;
    const colors = new Map<string, string>();
    for (const name of networkTrace.result.nodes) colors.set(name, NETWORK_TRACE_HIGHLIGHT);
    return colors;
  }, [networkTrace.result]);
  const networkTraceLinkColors = useMemo(() => {
    if (!networkTrace.result) return undefined;
    const colors = new Map<string, string>();
    for (const name of networkTrace.result.links) colors.set(name, NETWORK_TRACE_HIGHLIGHT);
    return colors;
  }, [networkTrace.result]);

  return {
    nodeVariablePeak,
    linkVariablePeak,
    subcatchmentVariablePeak,
    resultsAnimationNodeColors,
    resultsAnimationLinkColors,
    resultsAnimationSubcatchmentColors,
    thematicColorResult,
    thematicNodeColors,
    thematicLinkColors,
    thematicSubcatchmentColors,
    mapQueryAttributeDef,
    mapQueryValues,
    mapQueryMatches,
    mapQueryNodeColors,
    mapQueryLinkColors,
    mapQuerySubcatchmentColors,
    networkTraceNodeColors,
    networkTraceLinkColors,
  };
}
